import React from 'react'
import { NotificationComponentProps, registerNotification } from 'react-notifications'
import { useMediaQuery } from 'react-responsive'
import { useHistory } from '~/history'
import { Message, Sender } from '~/models'
import { ChatService, projectStore } from '~/stores'
import { ChatDescriptor } from '~/stores/chat'
import { Avatar } from '~/ui/app/media'
import { memo } from '~/ui/component'
import { screenMinWidths } from '~/ui/styling/layout'
import { useChatService } from '../ChatContainer'
import NotificationPanel, { imageSize } from './NotificationPanel'

export interface Props {
  message: Message
  sender:  Sender
  chat:    ChatDescriptor
}

const ChatMessageNotification = memo('ChatMessageNotification', (props: Props & NotificationComponentProps) => {

  const {message, sender, chat, hide} = props
  const service = useChatService()

  const isWidescreen = useMediaQuery({minWidth: screenMinWidths.widescreen})

  const history = useHistory()
  const goToChannelAndHide = React.useCallback(() => {
    service?.switchToChat(chat.uri)
    hide()

    // If we're on a widescreen, the chat is not on a separate page, so we don't need to navigate.
    if (isWidescreen) { return }
    history?.push('/chat')
  }, [chat.uri, hide, history, service, isWidescreen])

  const senderName = React.useMemo(
    () => [sender.firstName, sender.lastName].filter(Boolean).join(' '),
    [sender.firstName, sender.lastName],
  )

  //------
  // Rendering

  function render() {
    return (
      <NotificationPanel
        title={senderName}
        body={message.notificationText('received-short', sender)}
        avatar={renderAvatar()}
        onTap={goToChannelAndHide}
      />
    )
  }

  function renderAvatar() {
    return (
      <Avatar
        source={sender.photoURL}
        firstName={sender.firstName}
        lastName={sender.lastName}
        size={imageSize}
      />
    )
  }

  return render()

})

export default ChatMessageNotification

export function registerChatMessageNotification(service: ChatService) {
  return registerNotification(ChatMessageNotification, show => {
    return service.addIncomingMessageListener(({message, sender, chat}) => {
      if (sender.id === service.sender?.id) { return }
      if (isViewingChannel(chat.uri, service)) { return }

      const imageURL = sender.photoURL
        ?? (chat.image?.type === 'remote' ? chat.image.url : undefined)
        ?? undefined

      show(chat.uri, {
        message: message,
        sender:  sender,
        chat: chat,
      }, {
        browser: {
          icon:  imageURL,
          title: [sender.firstName, sender.lastName].filter(Boolean).join(' '),
          body:  message.notificationText('received-short', sender),
          tag:   chat.uri,
        },
      })
    })
  })
}

function isViewingChannel(chatID: string, chatService: ChatService) {
  if (!isChatVisible()) { return false }
  return chatService.currentChatUri === chatID
}

function isChatVisible() {
  const layout = projectStore.project?.web.layout

  if (layout === 'full') {
    return true
  } else {
    const url = new URL(document.location.href)
    return url.pathname === '/chat'
  }
}